package org.teddapp.project.ui.focus

import org.teddapp.project.ui.LocalTeddFonts

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.hoverable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsHoveredAsState
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.ReadOnlyComposable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.tedd.db.Pomodoro_check
import com.tedd.db.Pomodoro_item
import com.tedd.db.Pomodoro_session
import com.tedd.db.Task
import org.teddapp.project.db.TeddRepositories
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.pomodoro.PomPriority
import org.teddapp.project.ui.pomodoro.ResolvedTitle

/**
 * Helpers UI pour le Pomodoro Mac v2 — layout 2 colonnes :
 *  Session du jour avec tabs (centre) · Timer + Inspector (droite).
 *
 * Toute la logique de persistance reste dans les repos / PomodoroService.
 */

internal object FocusV2 {
    val GroupObjective: Color @Composable @ReadOnlyComposable get() = TeddTokens.ModGoals
    val GroupProject: Color @Composable @ReadOnlyComposable get() = TeddTokens.ModPortfolio
    val GroupQuick: Color @Composable @ReadOnlyComposable get() = TeddTokens.InkMuted
}

// ──────────────────────────────────────────────────────────────────────
// CENTER: PomodoroItemRow (one row in the session list)
// ──────────────────────────────────────────────────────────────────────

internal data class ItemMeta(
    val title: String,
    val sourceLabel: String, // OBJ / PRJ / QUICK / EX
    val context: String,
    /** Temps prévu en minutes, ou null quand rien n'a été fixé. */
    val minutes: Int? = null,
) {
    /** Repère de l'origine. Jamais seul : le sigle à côté dit la même chose. */
    val color: Color
        @Composable @ReadOnlyComposable
        get() = when (sourceLabel) {
            "OBJ" -> FocusV2.GroupObjective
            "PRJ" -> FocusV2.GroupProject
            "EX", "EX·T" -> TeddTokens.Warning
            else -> FocusV2.GroupQuick
        }
}

@Composable
internal fun PomodoroItemRow(
    item: Pomodoro_item,
    meta: ItemMeta,
    selected: Boolean,
    onSelect: () -> Unit,
    onToggle: () -> Unit,
    onStartTimer: () -> Unit,
    onSetPriority: (PomPriority) -> Unit,
    onRemove: () -> Unit,
    verifsDone: Int,
    verifsTotal: Int,
    /** Modification sur place ; proposée seulement pour une tâche rapide. */
    onEdit: (() -> Unit)? = null,
    /** Monter ou descendre d'une place parmi les tâches de même priorité ; null = pas possible. */
    onMoveUp: (() -> Unit)? = null,
    onMoveDown: (() -> Unit)? = null,
) {
    val prio = PomPriority.fromValue(item.priority)
    val done = item.completed != 0L
    var menuOpen by remember { mutableStateOf(false) }
    val clipboard = androidx.compose.ui.platform.LocalClipboardManager.current

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(TeddTokens.RSm))
            .background(
                if (selected) TeddTokens.LimeSoft else Color.Transparent,
                RoundedCornerShape(TeddTokens.RSm),
            )
            .border(
                1.dp,
                if (selected) TeddTokens.LimeStrong else Color.Transparent,
                RoundedCornerShape(TeddTokens.RSm),
            )
            .clickable(
                interactionSource = remember { MutableInteractionSource() },
                indication = null,
                onClick = onSelect,
            )
            .padding(horizontal = 12.dp, vertical = 10.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        // Checkbox (priority color border, gradient when done)
        Box(
            modifier = Modifier
                .size(20.dp)
                .clip(RoundedCornerShape(TeddTokens.RXs))
                .then(
                    if (done) Modifier.background(TeddTokens.SignatureGradient, RoundedCornerShape(TeddTokens.RXs))
                    else Modifier.border(1.5.dp, prio.color, RoundedCornerShape(TeddTokens.RXs))
                )
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = onToggle,
                ),
            contentAlignment = Alignment.Center,
        ) {
            if (done) TeddIcon(name = "check", size = 12.dp, color = TeddTokens.OnAction, strokeWidth = 3.dp)
        }
        // Priority pill
        Box(
            modifier = Modifier
                .size(20.dp)
                .clip(RoundedCornerShape(TeddTokens.RXs))
                .background(prio.color.copy(alpha = 0.1f), RoundedCornerShape(TeddTokens.RXs)),
            contentAlignment = Alignment.Center,
        ) {
            TeddIcon(name = prio.icon, size = 11.dp, color = prio.color, strokeWidth = 2.2.dp)
        }
        // Title + meta
        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = meta.title,
                color = if (done) TeddTokens.InkMuted else TeddTokens.Ink,
                fontSize = 13.sp,
                fontWeight = FontWeight.Medium,
                maxLines = 1,
                textDecoration = if (done) TextDecoration.LineThrough else TextDecoration.None,
            )
            Spacer(modifier = Modifier.height(2.dp))
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(5.dp),
            ) {
                Box(
                    modifier = Modifier
                        .clip(RoundedCornerShape(3.dp))
                        .background(meta.color.copy(alpha = 0.1f), RoundedCornerShape(3.dp))
                        .padding(horizontal = 5.dp, vertical = 1.dp),
                ) {
                    Text(
                        text = meta.sourceLabel,
                        color = meta.color,
                        fontSize = 9.sp,
                        fontWeight = FontWeight.Bold,
                        letterSpacing = 0.4.sp,
                    )
                }
                ItemMinutesTag(meta.minutes, done)
                if (meta.context.isNotEmpty()) {
                    Text(
                        text = meta.context,
                        color = TeddTokens.InkMuted,
                        fontSize = 10.5.sp,
                        maxLines = 1,
                        modifier = Modifier.weight(1f),
                    )
                } else {
                    Spacer(modifier = Modifier.weight(1f))
                }
                if (verifsTotal > 0) {
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(2.dp),
                    ) {
                        TeddIcon(name = "check", size = 9.dp, color = TeddTokens.InkMuted, strokeWidth = 2.4.dp)
                        Text(
                            text = "$verifsDone/$verifsTotal",
                            color = TeddTokens.InkMuted,
                            fontSize = 10.sp,
                            fontFamily = LocalTeddFonts.current.mono,
                        )
                    }
                }
            }
        }
        // Monter / descendre, à la souris : le même déplacement que le glisser.
        FocusMoveArrows(onMoveUp = onMoveUp, onMoveDown = onMoveDown)
        // Timer button
        Box(
            modifier = Modifier
                .size(28.dp)
                .clip(RoundedCornerShape(TeddTokens.RXs))
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = onStartTimer,
                ),
            contentAlignment = Alignment.Center,
        ) {
            TeddIcon(name = "clock", size = 13.dp, color = TeddTokens.InkMuted, strokeWidth = 2.dp)
        }
        // Menu (priority + remove)
        Box {
            Box(
                modifier = Modifier
                    .size(28.dp)
                    .clip(RoundedCornerShape(TeddTokens.RXs))
                    .background(
                        if (menuOpen) TeddTokens.SurfaceSunken else Color.Transparent,
                        RoundedCornerShape(TeddTokens.RXs),
                    )
                    .clickable(
                        interactionSource = remember { MutableInteractionSource() },
                        indication = null,
                        onClick = { menuOpen = true },
                    ),
                contentAlignment = Alignment.Center,
            ) {
                TeddIcon(name = "more", size = 14.dp, color = TeddTokens.InkMuted, strokeWidth = 2.4.dp)
            }
            if (menuOpen) {
                ActionMenuPopup(
                    currentPriority = PomPriority.fromValue(item.priority),
                    onPick = { p ->
                        onSetPriority(p)
                        menuOpen = false
                    },
                    onCopyName = {
                        clipboard.setText(androidx.compose.ui.text.AnnotatedString(meta.title))
                        menuOpen = false
                    },
                    onRemove = {
                        onRemove()
                        menuOpen = false
                    },
                    onEdit = onEdit?.takeIf { item.source_type == "quick" }?.let { edit -> { edit(); menuOpen = false } },
                    onDismiss = { menuOpen = false },
                )
            }
        }
    }
}

// ──────────────────────────────────────────────────────────────────────
// ACTION MENU POPUP — priority picker + remove
// Layout : entête "PRIORITÉ", 5 lignes priorité (carré 8×8 + libellé, ligne
// sélectionnée teintée à la couleur de la priorité), séparateur, "Retirer".
// ──────────────────────────────────────────────────────────────────────
@Composable
private fun ActionMenuPopup(
    currentPriority: PomPriority,
    onPick: (PomPriority) -> Unit,
    onCopyName: () -> Unit,
    onRemove: () -> Unit,
    onDismiss: () -> Unit,
    onEdit: (() -> Unit)? = null,
) {
    androidx.compose.ui.window.Popup(
        alignment = Alignment.TopEnd,
        offset = androidx.compose.ui.unit.IntOffset(0, 36),
        onDismissRequest = onDismiss,
        properties = androidx.compose.ui.window.PopupProperties(focusable = true),
    ) {
        Column(
            modifier = Modifier
                .width(208.dp)
                .clip(RoundedCornerShape(TeddTokens.RSm))
                .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
                .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
                .padding(6.dp),
        ) {
            Text(
                text = "PRIORITÉ",
                color = TeddTokens.InkMuted,
                fontSize = 10.sp,
                fontWeight = FontWeight.Bold,
                letterSpacing = 0.6.sp,
                modifier = Modifier.padding(start = 8.dp, end = 8.dp, top = 5.dp, bottom = 3.dp),
            )
            PomPriority.entries.forEach { p ->
                ActionMenuRow(
                    label = p.label,
                    color = p.color,
                    selected = p == currentPriority,
                    leading = {
                        Box(
                            modifier = Modifier
                                .size(8.dp)
                                .clip(RoundedCornerShape(2.dp))
                                .background(p.color, RoundedCornerShape(2.dp)),
                        )
                    },
                    onClick = { onPick(p) },
                )
            }
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(1.dp)
                    .padding(horizontal = 2.dp),
            ) {
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .background(TeddTokens.Line),
                )
            }
            Spacer(modifier = Modifier.height(4.dp))
            if (onEdit != null) {
                ActionMenuRow(
                    label = "Modifier",
                    color = TeddTokens.Ink,
                    selected = false,
                    leading = {
                        TeddIcon(name = "edit", size = 11.dp, color = TeddTokens.InkMuted, strokeWidth = 2.dp)
                    },
                    onClick = onEdit,
                )
            }
            ActionMenuRow(
                label = "Copier le nom",
                color = TeddTokens.Ink,
                selected = false,
                leading = {
                    TeddIcon(name = "note", size = 11.dp, color = TeddTokens.InkMuted, strokeWidth = 2.dp)
                },
                onClick = onCopyName,
            )
            ActionMenuRow(
                label = "Retirer de la session",
                color = TeddTokens.Danger,
                selected = false,
                danger = true,
                leading = {
                    TeddIcon(name = "trash", size = 11.dp, color = TeddTokens.Danger, strokeWidth = 2.dp)
                },
                onClick = onRemove,
            )
        }
    }
}

@Composable
private fun ActionMenuRow(
    label: String,
    color: Color,
    selected: Boolean,
    danger: Boolean = false,
    leading: @Composable () -> Unit,
    onClick: () -> Unit,
) {
    val interactionSource = remember { MutableInteractionSource() }
    val hovered by interactionSource.collectIsHoveredAsState()
    val bg = when {
        selected -> color.copy(alpha = 0.1f)
        hovered && danger -> color.copy(alpha = 0.08f)
        hovered -> TeddTokens.SurfaceSunken
        else -> Color.Transparent
    }
    val textColor = when {
        selected -> color
        danger -> TeddTokens.Danger
        else -> TeddTokens.Ink
    }
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(TeddTokens.RXs))
            .background(bg, RoundedCornerShape(TeddTokens.RXs))
            .hoverable(interactionSource)
            .clickable(
                interactionSource = interactionSource,
                indication = null,
                onClick = onClick,
            )
            .padding(horizontal = 8.dp, vertical = 6.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        Box(
            modifier = Modifier.size(12.dp),
            contentAlignment = Alignment.Center,
        ) { leading() }
        Text(
            text = label,
            color = textColor,
            fontSize = 12.sp,
            fontWeight = if (selected) FontWeight.SemiBold else FontWeight.Medium,
        )
    }
}

// ──────────────────────────────────────────────────────────────────────
// CENTER: HistoryView (full session cards with items)
// ──────────────────────────────────────────────────────────────────────

@Composable
internal fun HistoryView(
    repos: TeddRepositories,
    sessions: List<Pomodoro_session>,
    resolveMeta: (Pomodoro_item) -> ItemMeta,
    onClone: (sessionId: String) -> Unit,
) {
    if (sessions.isEmpty()) {
        Column(
            modifier = Modifier.fillMaxSize().padding(32.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center,
        ) {
            Box(
                modifier = Modifier
                    .size(48.dp)
                    .clip(RoundedCornerShape(TeddTokens.RMd))
                    .background(TeddTokens.LimeSoft, RoundedCornerShape(TeddTokens.RMd)),
                contentAlignment = Alignment.Center,
            ) {
                TeddIcon(name = "clock", size = 22.dp, color = TeddTokens.LimeStrong, strokeWidth = 1.6.dp)
            }
            Spacer(modifier = Modifier.height(12.dp))
            Text(
                "Pas encore d'historique",
                color = TeddTokens.Ink,
                fontSize = 15.sp,
                fontWeight = FontWeight.SemiBold,
            )
            Spacer(modifier = Modifier.height(4.dp))
            Text(
                "Tes sessions passées apparaîtront ici dès demain.",
                color = TeddTokens.InkMuted,
                fontSize = 12.sp,
            )
        }
        return
    }
    LazyColumn(
        modifier = Modifier.fillMaxSize(),
        contentPadding = PaddingValues(horizontal = 16.dp, vertical = 16.dp),
        verticalArrangement = Arrangement.spacedBy(12.dp),
    ) {
        item(key = "hdr") {
            Text(
                buildHistoryHeader(sessions.size),
                color = TeddTokens.InkMuted,
                fontSize = 11.5.sp,
                modifier = Modifier.padding(horizontal = 4.dp, vertical = 4.dp),
            )
        }
        items(sessions, key = { it.id }) { s ->
            HistorySessionCard(
                repos = repos,
                session = s,
                resolveMeta = resolveMeta,
                onClone = { onClone(s.id) },
            )
        }
    }
}

private fun buildHistoryHeader(n: Int): String {
    val plural = if (n > 1) "s" else ""
    return "$n session$plural passée$plural · clic « Cloner » pour reprendre."
}

@Composable
private fun HistorySessionCard(
    repos: TeddRepositories,
    session: Pomodoro_session,
    resolveMeta: (Pomodoro_item) -> ItemMeta,
    onClone: () -> Unit,
) {
    val items by repos.pomodoroItems.observeBySession(session.id).collectAsState(emptyList())
    val total = items.size
    val done = items.count { it.completed != 0L }
    val pct = if (total == 0) 0 else done * 100 / total

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(TeddTokens.RSm))
            .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
            .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm)),
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .border(1.dp, TeddTokens.Line, RoundedCornerShape(0.dp))
                .padding(horizontal = 14.dp, vertical = 12.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            Box(
                modifier = Modifier
                    .size(36.dp)
                    .clip(RoundedCornerShape(TeddTokens.RSm))
                    .background(TeddTokens.SignatureGradient, RoundedCornerShape(TeddTokens.RSm)),
                contentAlignment = Alignment.Center,
            ) {
                Text(
                    text = "$pct%",
                    color = TeddTokens.OnAction,
                    fontSize = 12.sp,
                    fontWeight = FontWeight.Bold,
                    fontFamily = LocalTeddFonts.current.mono,
                )
            }
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = dayLabelForHistory(session.date_start),
                    color = TeddTokens.Ink,
                    fontSize = 14.sp,
                    fontWeight = FontWeight.SemiBold,
                )
                Text(
                    text = buildSessionSubtitle(session, done, total),
                    color = TeddTokens.InkMuted,
                    fontSize = 11.sp,
                    fontFamily = LocalTeddFonts.current.mono,
                )
            }
            TeddButton(
                text = "Cloner",
                onClick = onClone,
                kind = TeddButtonKind.Secondary,
                size = TeddButtonSize.Sm,
                leadingIcon = {
                    TeddIcon(name = "arrow", size = 11.dp, color = TeddTokens.InkMuted, strokeWidth = 2.4.dp)
                },
            )
        }
        Column(modifier = Modifier.padding(8.dp)) {
            items.sortedBy { it.order_index }.forEach { it ->
                HistoryItemRow(item = it, meta = resolveMeta(it))
            }
        }
    }
}

private fun buildSessionSubtitle(s: Pomodoro_session, done: Int, total: Int): String {
    val base = "${s.date_start} · $done/$total · ${s.timer_minutes} min"
    return if (s.status == "completed") "$base · clôturée" else base
}

@Composable
private fun HistoryItemRow(item: Pomodoro_item, meta: ItemMeta) {
    val done = item.completed != 0L
    val prio = PomPriority.fromValue(item.priority)
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 8.dp, vertical = 5.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        Box(
            modifier = Modifier
                .size(14.dp)
                .clip(RoundedCornerShape(TeddTokens.RXs))
                .then(
                    if (done) Modifier.background(TeddTokens.SignatureGradient, RoundedCornerShape(TeddTokens.RXs))
                    else Modifier.border(1.5.dp, prio.color, RoundedCornerShape(TeddTokens.RXs))
                ),
            contentAlignment = Alignment.Center,
        ) {
            if (done) TeddIcon(name = "check", size = 9.dp, color = TeddTokens.OnAction, strokeWidth = 3.dp)
        }
        Box(
            modifier = Modifier
                .clip(RoundedCornerShape(3.dp))
                .background(meta.color.copy(alpha = 0.1f), RoundedCornerShape(3.dp))
                .padding(horizontal = 4.dp, vertical = 1.dp),
        ) {
            Text(
                text = meta.sourceLabel,
                color = meta.color,
                fontSize = 9.sp,
                fontWeight = FontWeight.Bold,
                letterSpacing = 0.4.sp,
            )
        }
        ItemMinutesTag(meta.minutes, done, fontSize = 10.sp)
        Text(
            text = meta.title,
            color = if (done) TeddTokens.InkMuted else TeddTokens.Ink,
            fontSize = 12.sp,
            fontWeight = FontWeight.Medium,
            textDecoration = if (done) TextDecoration.LineThrough else TextDecoration.None,
            modifier = Modifier.weight(1f),
            maxLines = 1,
        )
        Box(
            modifier = Modifier
                .size(6.dp)
                .clip(CircleShape)
                .background(prio.color, CircleShape),
        )
    }
}

private fun dayLabelForHistory(iso: String): String {
    // Aligné sur les autres modules : aucune lib temps, on rend la date telle quelle
    // accompagnée d'un libellé relatif simple (Hier / Il y a N jours / date) calé
    // sur FOCUS_TODAY_ISO. Garde le format français.
    val today = ORG_TEDD_TODAY
    if (iso == today) return "Aujourd'hui"
    val diff = isoDayDiff(iso, today)
    return when {
        diff == 1 -> "Hier"
        diff in 2..6 -> "Il y a $diff jours"
        else -> iso
    }
}

// Date du jour — délégué au clock central.
internal val ORG_TEDD_TODAY: String
    get() = org.teddapp.project.db.TODAY_ISO

private fun isoDayDiff(a: String, b: String): Int {
    // Approche grossière sans kotlinx-datetime : YYYY-MM-DD → days depuis 1970.
    val da = isoToDays(a) ?: return 0
    val db = isoToDays(b) ?: return 0
    return db - da
}

private fun isoToDays(iso: String): Int? {
    val p = iso.split("-")
    if (p.size != 3) return null
    val y = p[0].toIntOrNull() ?: return null
    val m = p[1].toIntOrNull() ?: return null
    val d = p[2].toIntOrNull() ?: return null
    // Algorithme Howard Hinnant — civil_to_days.
    val yAdj = if (m <= 2) y - 1 else y
    val era = if (yAdj >= 0) yAdj / 400 else (yAdj - 399) / 400
    val yoe = yAdj - era * 400
    val mo = if (m > 2) m - 3 else m + 9
    val doy = (153 * mo + 2) / 5 + d - 1
    val doe = yoe * 365 + yoe / 4 - yoe / 100 + doy
    return era * 146097 + doe - 719468
}

// ──────────────────────────────────────────────────────────────────────
// RIGHT: Item inspector with verifications checklist
// ──────────────────────────────────────────────────────────────────────

@Composable
internal fun ItemInspector(
    item: Pomodoro_item,
    meta: ItemMeta,
    checks: List<Pomodoro_check>,
    onToggleCheck: (id: String, checked: Boolean) -> Unit,
    onAddCheck: (label: String) -> Unit,
    onRemoveCheck: (id: String) -> Unit,
    onEdit: (() -> Unit)? = null,
) {
    var draft by remember(item.id) { mutableStateOf("") }
    val done = checks.count { it.checked != 0L }
    val total = checks.size

    Column(
        modifier = Modifier
            .fillMaxSize()
            .padding(16.dp),
        verticalArrangement = Arrangement.spacedBy(12.dp),
    ) {
        Column {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(
                    text = "DÉTAIL DE L'ITEM",
                    color = TeddTokens.InkMuted,
                    fontSize = 10.sp,
                    fontWeight = FontWeight.Bold,
                    letterSpacing = 0.5.sp,
                    modifier = Modifier.weight(1f),
                )
                if (onEdit != null && item.source_type == "quick") {
                    TeddButton(text = "Modifier", onClick = onEdit, kind = TeddButtonKind.Ghost, size = TeddButtonSize.Sm)
                }
            }
            Spacer(modifier = Modifier.height(4.dp))
            Text(
                text = meta.title,
                color = TeddTokens.Ink,
                fontSize = 14.5.sp,
                fontWeight = FontWeight.SemiBold,
            )
            Spacer(modifier = Modifier.height(3.dp))
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(5.dp),
            ) {
                Box(
                    modifier = Modifier
                        .clip(RoundedCornerShape(3.dp))
                        .background(meta.color.copy(alpha = 0.1f), RoundedCornerShape(3.dp))
                        .padding(horizontal = 5.dp, vertical = 1.dp),
                ) {
                    Text(
                        text = meta.sourceLabel,
                        color = meta.color,
                        fontSize = 9.sp,
                        fontWeight = FontWeight.Bold,
                        letterSpacing = 0.4.sp,
                    )
                }
                ItemMinutesTag(meta.minutes, item.completed != 0L, fontSize = 11.sp)
                if (meta.context.isNotEmpty()) {
                    Text(
                        text = meta.context,
                        color = TeddTokens.InkMuted,
                        fontSize = 11.sp,
                        maxLines = 2,
                    )
                }
            }
        }
        Column {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(
                    text = "VÉRIFICATIONS",
                    color = TeddTokens.InkMuted,
                    fontSize = 10.sp,
                    fontWeight = FontWeight.Bold,
                    letterSpacing = 0.5.sp,
                    modifier = Modifier.weight(1f),
                )
                if (total > 0) {
                    Text(
                        text = "$done/$total",
                        color = TeddTokens.InkMuted,
                        fontSize = 11.sp,
                        fontFamily = LocalTeddFonts.current.mono,
                        fontWeight = FontWeight.Medium,
                    )
                }
            }
            Spacer(modifier = Modifier.height(6.dp))
            if (checks.isEmpty()) {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .clip(RoundedCornerShape(TeddTokens.RSm))
                        .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
                        .padding(10.dp),
                ) {
                    Text(
                        "Ajoute des critères pour valider avant de cocher.",
                        color = TeddTokens.InkMuted,
                        fontSize = 11.5.sp,
                    )
                }
            } else {
                Column(verticalArrangement = Arrangement.spacedBy(2.dp)) {
                    checks.forEach { c ->
                        VerifRow(check = c, onToggle = { onToggleCheck(c.id, c.checked == 0L) }, onRemove = { onRemoveCheck(c.id) })
                    }
                }
            }
            Spacer(modifier = Modifier.height(6.dp))
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(TeddTokens.RSm))
                    .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
                    .padding(horizontal = 9.dp, vertical = 6.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(6.dp),
            ) {
                TeddIcon(name = "plus", size = 11.dp, color = TeddTokens.InkMuted)
                BasicTextField(
                    value = draft,
                    onValueChange = { draft = it },
                    modifier = Modifier.weight(1f),
                    singleLine = true,
                    textStyle = TextStyle(color = TeddTokens.Ink, fontSize = 11.5.sp),
                    cursorBrush = SolidColor(TeddTokens.LimeStrong),
                    decorationBox = { inner ->
                        if (draft.isEmpty()) Text("Ajouter une vérification…", color = TeddTokens.InkMuted, fontSize = 11.5.sp)
                        inner()
                    },
                )
                TeddButton(
                    text = "+",
                    onClick = {
                        val t = draft.trim()
                        if (t.isNotEmpty()) {
                            onAddCheck(t); draft = ""
                        }
                    },
                    kind = TeddButtonKind.Secondary,
                    size = TeddButtonSize.Sm,
                    enabled = draft.trim().isNotEmpty(),
                )
            }
        }
    }
}

@Composable
private fun VerifRow(check: Pomodoro_check, onToggle: () -> Unit, onRemove: () -> Unit) {
    val checked = check.checked != 0L
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 7.dp, vertical = 5.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        Box(
            modifier = Modifier
                .size(15.dp)
                .clip(RoundedCornerShape(TeddTokens.RXs))
                .then(
                    if (checked) Modifier.background(TeddTokens.SignatureGradient, RoundedCornerShape(TeddTokens.RXs))
                    else Modifier.border(1.5.dp, TeddTokens.InkSubtle, RoundedCornerShape(TeddTokens.RXs))
                )
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = onToggle,
                ),
            contentAlignment = Alignment.Center,
        ) {
            if (checked) TeddIcon(name = "check", size = 9.dp, color = TeddTokens.OnAction, strokeWidth = 3.dp)
        }
        Text(
            text = check.label,
            color = if (checked) TeddTokens.InkMuted else TeddTokens.Ink,
            fontSize = 11.5.sp,
            textDecoration = if (checked) TextDecoration.LineThrough else TextDecoration.None,
            modifier = Modifier.weight(1f),
        )
        Box(
            modifier = Modifier
                .size(18.dp)
                .clip(RoundedCornerShape(TeddTokens.RXs))
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = onRemove,
                ),
            contentAlignment = Alignment.Center,
        ) {
            TeddIcon(name = "trash", size = 10.dp, color = TeddTokens.InkMuted)
        }
    }
}

// ──────────────────────────────────────────────────────────────────────
// Title resolver helpers — adapt PomodoroHelpers' ResolvedTitle into ItemMeta
// ──────────────────────────────────────────────────────────────────────

/**
 * Le temps prévu d'un item, « 25 min », posé à côté de son étiquette de source.
 * Rien n'est affiché quand aucun temps n'a été fixé : l'absence est une information.
 */
@Composable
internal fun ItemMinutesTag(minutes: Int?, done: Boolean = false, fontSize: androidx.compose.ui.unit.TextUnit = 10.5.sp) {
    val m = minutes?.takeIf { it > 0 } ?: return
    Text(
        text = "$m min",
        color = if (done) TeddTokens.InkMuted else TeddTokens.InkMuted,
        fontSize = fontSize,
        fontWeight = FontWeight.Medium,
        maxLines = 1,
    )
}

internal fun metaFromItem(item: Pomodoro_item, resolved: ResolvedTitle): ItemMeta {
    val label = when (item.source_type) {
        "action" -> "OBJ"
        "project_task" -> "PRJ"
        "quick" -> "QUICK"
        "exercise" -> "EX"
        "exercise_task" -> "EX·T"
        else -> "?"
    }
    return ItemMeta(
        title = resolved.title,
        sourceLabel = label,
        context = resolved.context,
        minutes = resolved.minutes,
    )
}

// ──────────────────────────────────────────────────────────────────────
// Misc UI: extension dialog, close button
// ──────────────────────────────────────────────────────────────────────

@Composable
internal fun ExtendDialog(
    initialEndDate: String?,
    onClose: () -> Unit,
    onSave: (endDate: String) -> Unit,
    onCancelExtension: (() -> Unit)?,
) {
    var draft by remember { mutableStateOf(initialEndDate ?: ORG_TEDD_TODAY) }
    Box(
        modifier = Modifier.fillMaxSize().background(Color.Black.copy(alpha = 0.42f)),
        contentAlignment = Alignment.Center,
    ) {
        Column(
            modifier = Modifier
                .width(420.dp)
                .clip(RoundedCornerShape(TeddTokens.RMd))
                .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RMd))
                .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RMd))
                .padding(22.dp),
        ) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(12.dp),
            ) {
                Box(
                    modifier = Modifier
                        .size(40.dp)
                        .clip(RoundedCornerShape(TeddTokens.RSm))
                        .background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RSm)),
                    contentAlignment = Alignment.Center,
                ) {
                    TeddIcon(name = "calendar", size = 18.dp, color = TeddTokens.ModFocus, strokeWidth = 2.dp)
                }
                Column(modifier = Modifier.weight(1f)) {
                    Text("Étendre la session", color = TeddTokens.Ink, fontSize = 15.sp, fontWeight = FontWeight.SemiBold)
                    Spacer(modifier = Modifier.height(1.dp))
                    Text(
                        "Bloc focus multi-jours.",
                        color = TeddTokens.InkMuted,
                        fontSize = 11.5.sp,
                    )
                }
            }
            Spacer(modifier = Modifier.height(14.dp))
            Text(
                "ÉTENDRE JUSQU'AU",
                color = TeddTokens.InkMuted,
                fontSize = 11.sp,
                fontWeight = FontWeight.SemiBold,
                letterSpacing = 0.5.sp,
            )
            Spacer(modifier = Modifier.height(6.dp))
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(38.dp)
                    .clip(RoundedCornerShape(TeddTokens.RSm))
                    .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
                    .background(TeddTokens.Bg, RoundedCornerShape(TeddTokens.RSm))
                    .padding(horizontal = 12.dp),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                BasicTextField(
                    value = draft,
                    onValueChange = { v ->
                        if (v.length <= 10 && v.all { it.isDigit() || it == '-' }) draft = v
                    },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true,
                    textStyle = TextStyle(color = TeddTokens.Ink, fontSize = 13.sp, fontFamily = LocalTeddFonts.current.mono),
                    cursorBrush = SolidColor(TeddTokens.LimeStrong),
                )
            }
            Spacer(modifier = Modifier.height(16.dp))
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(6.dp),
            ) {
                if (initialEndDate != null && onCancelExtension != null) {
                    TeddButton(
                        text = "Annuler l'extension",
                        onClick = onCancelExtension,
                        kind = TeddButtonKind.Ghost,
                        size = TeddButtonSize.Sm,
                    )
                }
                Spacer(modifier = Modifier.weight(1f))
                TeddButton(text = "Fermer", onClick = onClose, kind = TeddButtonKind.Secondary, size = TeddButtonSize.Sm)
                TeddButton(text = "Étendre", onClick = { onSave(draft) }, kind = TeddButtonKind.Primary, size = TeddButtonSize.Sm)
            }
        }
    }
}

@Composable
internal fun XCloseButton(size: Int = 22, color: Color = TeddTokens.InkMuted, onClick: () -> Unit) {
    Box(
        modifier = Modifier
            .size(size.dp)
            .clip(RoundedCornerShape(TeddTokens.RXs))
            .clickable(
                interactionSource = remember { MutableInteractionSource() },
                indication = null,
                onClick = onClick,
            ),
        contentAlignment = Alignment.Center,
    ) {
        Canvas(modifier = Modifier.size((size - 8).dp)) {
            val s = this.size.minDimension
            drawLine(
                color = color,
                start = Offset(0f, 0f),
                end = Offset(s, s),
                strokeWidth = 1.6.dp.toPx(),
                cap = StrokeCap.Round,
            )
            drawLine(
                color = color,
                start = Offset(s, 0f),
                end = Offset(0f, s),
                strokeWidth = 1.6.dp.toPx(),
                cap = StrokeCap.Round,
            )
        }
    }
}

/**
 * Deux petites flèches, l'une sur l'autre. Une flèche sans action (première ou dernière
 * de son groupe de priorité) reste visible, mais éteinte : la colonne garde sa largeur.
 */
@Composable
internal fun FocusMoveArrows(onMoveUp: (() -> Unit)?, onMoveDown: (() -> Unit)?) {
    if (onMoveUp == null && onMoveDown == null) return
    Column(horizontalAlignment = Alignment.CenterHorizontally) {
        FocusMoveArrow(rotation = -90f, onClick = onMoveUp)
        FocusMoveArrow(rotation = 90f, onClick = onMoveDown)
    }
}

@Composable
private fun FocusMoveArrow(rotation: Float, onClick: (() -> Unit)?) {
    Box(
        modifier = Modifier
            .size(width = 22.dp, height = 14.dp)
            .clip(RoundedCornerShape(TeddTokens.RXs))
            .then(
                if (onClick != null) Modifier.clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = onClick,
                ) else Modifier,
            ),
        contentAlignment = Alignment.Center,
    ) {
        Box(modifier = Modifier.size(10.dp).rotate(rotation)) {
            TeddIcon(
                name = "chevron",
                size = 10.dp,
                color = if (onClick != null) TeddTokens.InkMuted else TeddTokens.Line,
                strokeWidth = 2.2.dp,
            )
        }
    }
}
